import type { CityConfig } from "../city-template/types";

export const haywardConfig: CityConfig = {
  city: "Hayward",
  state: "California",
  stateAbbr: "CA",
  slug: "hayward",
  route: "/cities/hayward",
  title: "Emergency Passport Hayward, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Hayward, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Alameda County and East Bay travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Hayward, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Hayward and Alameda County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Hayward%20California%20cityscape%20at%20golden%20hour%20with%20rolling%20East%20Bay%20hills%20in%20background%2C%20modern%20downtown%20buildings%20along%20Mission%20Boulevard%2C%20warm%20sunset%20light%20illuminating%20the%20San%20Francisco%20Bay%20Area%20landscape%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20suburban%20city%20environment%20with%20warm%20amber%20and%20orange%20tones&width=1800&height=700&seq=hayward-hero-001&orientation=landscape",
  heroImageAlt: "Hayward California skyline at golden hour with East Bay hills and downtown buildings",
  geoPosition: "37.6688;-122.0810",
  geoPlacename: "Hayward, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown SF — approximately 30–40 minutes from Hayward via I-880",
  },
  nearbyCitiesZips: [
    { name: "Hayward", zips: "94541–94545" },
    { name: "San Leandro", zips: "94577–94579" },
    { name: "Castro Valley", zips: "94546, 94552" },
    { name: "Union City", zips: "94587" },
    { name: "Fremont", zips: "94536, 94538, 94539, 94555" },
    { name: "Newark", zips: "94560" },
    { name: "San Lorenzo", zips: "94580" },
    { name: "Pleasanton", zips: "94566, 94588" },
    { name: "Dublin", zips: "94568" },
    { name: "Livermore", zips: "94550, 94551" },
    { name: "Oakland", zips: "94601–94612" },
    { name: "Berkeley", zips: "94701–94710" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Alameda County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Berkeley", route: "/cities/berkeley" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "Daly City", route: "/cities/daly-city" },
    { name: "Fremont", route: "#" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Hayward remotely via secure courier. We do not have a walk-in office in Hayward — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Alameda County travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Alameda County",
  serviceAreaIntro: "From the downtown district to the Hayward Hills, from the industrial area to the university district — Fast Passport Center serves the entire Hayward community. We regularly help travelers from San Leandro, Castro Valley, Union City, and Fremont get their passports fast.",
  nearbyCitiesIntro: "We serve all Hayward neighborhoods and nearby East Bay cities including San Leandro, Castro Valley, Union City, Fremont, Newark, and San Lorenzo. If you are in Alameda County or nearby Contra Costa County, we have you covered.",
  howItWorksIntro: "Most Hayward travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Oakland International Airport serving as the closest major airport, East Bay travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Hayward and Alameda County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Hayward? Here are the most common ones we hear from Alameda County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Alameda County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Maria S.",
      location: "Hayward, CA",
      rating: 5,
      text: "My flight to Mexico was in 2 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. Absolutely saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "David L.",
      location: "San Leandro, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Tokyo. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for East Bay residents.",
      service: "Passport Renewal",
    },
    {
      name: "Angela T.",
      location: "Castro Valley, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Kevin R.",
      location: "Union City, CA",
      rating: 5,
      text: "As a frequent international traveler from the East Bay, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "hayward-1",
      name: "Hayward Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2417 Industrial Pkwy",
      city: "Hayward",
      state: "CA",
      zip: "94545",
      phone: "(510) 786-0665",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Hayward",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "hayward-2",
      name: "San Leandro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 W Juana Ave",
      city: "San Leandro",
      state: "CA",
      zip: "94577",
      phone: "(510) 352-2944",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown San Leandro",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for San Leandro residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "hayward-3",
      name: "Fremont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37010 Dusterberry Way",
      city: "Fremont",
      state: "CA",
      zip: "94536",
      phone: "(510) 797-5419",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Fremont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "hayward-4",
      name: "Union City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "31090 Dyer St",
      city: "Union City",
      state: "CA",
      zip: "94587",
      phone: "(510) 471-1215",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Union City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Union City and southern Alameda County residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "hayward-5",
      name: "Castro Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20720 Redwood Rd",
      city: "Castro Valley",
      state: "CA",
      zip: "94546",
      phone: "(510) 538-0234",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from downtown Castro Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Castro Valley and eastern Alameda County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "hayward-6",
      name: "Alameda County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1106 Madison St",
      city: "Oakland",
      state: "CA",
      zip: "94607",
      phone: "(510) 272-6362",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "0.3 miles from the Oakland courthouse",
      website: "https://www.acgov.org/",
      websiteLabel: "Alameda County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "East Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Alameda County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Hayward applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Alameda County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Alameda County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Hayward." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Hayward address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Hayward before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Hayward for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most East Bay travelers." },
    { q: "How fast can I renew my passport in Hayward?", a: "Fast Passport Center offers fast passport renewal in Hayward in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Hayward in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Hayward?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Hayward residents." },
    { q: "Can I expedite a child passport in Hayward?", a: "Yes. Fast Passport Center expedites child passports in Hayward for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Hayward with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Hayward?",
      a: "With Fast Passport Center's emergency courier service, Hayward travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Hayward?",
      a: "Popular options include the Hayward Post Office (94545), San Leandro Post Office (94577), Fremont Post Office (94536), and Alameda County Clerk (94607). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Hayward?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Fremont, Union City, or Castro Valley?",
      a: "We serve all of Alameda County including Fremont, Union City, Castro Valley, San Leandro, Newark, and San Lorenzo. Our overnight shipping labels work from any UPS location, so your location within the East Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Hayward?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Hayward?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Hayward, San Leandro, and Fremont offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Hayward?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Hayward?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Alameda County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Hayward", address: "24900 Santa Clara St, Hayward, CA 94542", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Hayward", address: "880 W Winton Ave, Hayward, CA 94545", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Hayward", address: "24124 Hesperian Blvd, Hayward, CA 94545", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — San Leandro", address: "1500 E 14th St, San Leandro, CA 94577", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Fremont", address: "44009 Osgood Rd, Fremont, CA 94539", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Castro Valley", address: "20871 Redwood Rd, Castro Valley, CA 94546", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Union City", address: "32114 Alvarado Blvd, Union City, CA 94587", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Hayward", address: "28505 Hesperian Blvd, Hayward, CA 94545", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Hayward",
      address: "1500 W Winton Ave, Hayward, CA 94545",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Leandro",
      address: "1500 E 14th St, San Leandro, CA 94577",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Fremont",
      address: "4287 Central Ave, Fremont, CA 94536",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Oakland",
      address: "5300 Claremont Ave, Oakland, CA 94618",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Oakland and Berkeley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "8 miles from Hayward",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Convenient for East Bay travelers. Domestic and international flights to Mexico and beyond.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "25 miles from Hayward",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "22 miles from Hayward",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Convenient for Silicon Valley and South Bay travelers. International flights to Asia and Europe.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Alameda County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Alameda County",
      url: "https://www.acgov.org/auditor/clerk/",
    },
    {
      label: "Alameda County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Alameda County",
      url: "https://www.alameda.courts.ca.gov/",
    },
  ],
};